import { Tag } from '@loadsmart/loadsmart-ui'
import { Button, Divider, Layout, Text } from '@loadsmart/miranda-react'

import { Icon } from '../icon/Icon'

export type FilterSummaryItem = {
  /**
   * An unique ID that will be returned by the `onClearItem` callback to
   * identify which item was cleared.
   */
  id: string

  /**
   * Optional label to be rendered prefixed to the value
   *
   * @example
   * ```js
   * const item: FilterSummaryItem = {
   *     id: 'mode',
   *     label: 'Mode',
   *     value: 'LTL',
   * }
   * ```
   * For the provided item above, the following text will be rendered
   * `Mode: LTL`
   *
   */
  label?: string

  /**
   * Value to be rendered for this item
   */
  value: string

  /**
   * If `true`, the remove button on this particular item will *not* be rendered
   */
  nonRemovable?: boolean
}

export type FilterSummaryProps = {
  /**
   * List of items to be rendered
   */
  items?: Array<FilterSummaryItem>

  /**
   * Callback to be called when the "Clear all filters" is clicked. If not
   * provided, the button will not be rendered.
   */
  onClearAll?: () => void

  /**
   * Callback to be called when the close button is clicked on one of the
   * rendered items. If not provided, none of the items will be removable.
   */
  onClearItem?: (id: string) => void

  /**
   * Optional. Extra actions to be rendered on the right of the
   * "Clear all filters" button.
   */
  extraActions?: React.ReactNode
}

export function FilterSummary({
  items,
  onClearItem,
  onClearAll,
  extraActions,
}: FilterSummaryProps) {
  const hasItems = Array.isArray(items) && items.length > 0
  const hasClearAll = typeof onClearAll === 'function'
  const hasClearItem = typeof onClearItem === 'function'

  if (!hasItems) {
    return null
  }

  return (
    <Layout.Box
      backgroundColor="color-background-primary"
      borderColor="color-border"
      borderRadius="border-radius-s"
      borderWidth="border-thin"
      padding="spacing-4"
      style={{ display: 'block' }}
    >
      <Layout.Stack gap="spacing-4">
        <Layout.Group gap="spacing-4" align="center" justify="space-between">
          <Text variant="body-md-bold">Filtering by:</Text>
          <Layout.Group gap="spacing-4" align="center">
            {hasClearAll && (
              <Button
                type="button"
                variant="tertiary"
                size="small"
                leading={<Icon name="trash" />}
                onClick={onClearAll}
              >
                Clear all filters
              </Button>
            )}
            {extraActions}
          </Layout.Group>
        </Layout.Group>
        <Divider />
        <Layout.Group gap="spacing-2">
          {items.map(({ id, label, value, nonRemovable = false }) => (
            <Tag
              key={id}
              size="default"
              variant="default"
              removable={hasClearItem && !nonRemovable}
              onRemove={() => onClearItem?.(id)}
            >
              {label ? `${label}: ` : ''}
              {value}
            </Tag>
          ))}
        </Layout.Group>
      </Layout.Stack>
    </Layout.Box>
  )
}
